import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { BaseComponentProps } from "../types";

import type { UiComponent } from "@/features/integrations/types";

interface KeyValueField {
  label: string;
  dataKey?: string;
  valueComponent?: UiComponent;
}

export interface KeyValueListProps {
  fields: KeyValueField[];
}

type KeyValueListComponentProps = BaseComponentProps & KeyValueListProps;

function KeyValueListComponent({ fields, data, renderWithContext }: KeyValueListComponentProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2.5 text-sm">
      {fields.map((field, index) => {
        let valueContent: ReactNode;

        if (field.valueComponent) {
          const rendered = renderWithContext(field.valueComponent, { data: data ?? {} });
          if (!rendered) return null;
          valueContent = rendered;
        } else if (field.dataKey) {
          const rawValue = data?.[field.dataKey];
          if (rawValue === null || rawValue === undefined || String(rawValue).trim() === "") return null;
          const stringValue = String(rawValue);
          valueContent = stringValue.includes(".")
            ? t(stringValue, { defaultValue: stringValue })
            : stringValue;
        } else {
          return null;
        }

        return (
          <div key={field.dataKey ?? index} className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground shrink-0 font-medium">{t(field.label)}</span>
            <span className="text-right font-medium truncate">{valueContent}</span>
          </div>
        );
      })}
    </div>
  );
}

export { KeyValueListComponent };
